import ApplicationController from '../application-controller';

import borderTop from '~/../../snippets/mm-cta-button--border-t.liquid?raw';
import borderBottom from '~/../../snippets/mm-cta-button--border-b.liquid?raw';
import borderLeft from '~/../../snippets/mm-cta-button--border-l.liquid?raw';
import borderRight from '~/../../snippets/mm-cta-button--border-r.liquid?raw';

interface TitleLine {
  content: string;
  index: number;
}

const lineStyles = ['l--barlow', 'l--termina', 'm--ambroise'];

export default class extends ApplicationController {
  declare globalObserver?: MutationObserver;
  declare titleObserver?: MutationObserver;
  declare buttonObserver?: MutationObserver;

  connect() {
    super.connect();
    this.setupGlobalObserver();
  }

  get popupElement() {
    return document.getElementById('restock-rocket');
  }

  get popupTitleElement() {
    return document.getElementById('form-Product-Name');
  }

  get popupButtonElement() {
    return document.getElementById('form-Button');
  }

  setupGlobalObserver() {
    this.globalObserver = new MutationObserver(this.onGlobalObserve);
    this.globalObserver.observe(this.element, {
      subtree: true,
      childList: true,
    });
  }

  onGlobalObserve: MutationCallback = (_, observer) => {
    if (
      this.popupElement &&
      this.popupTitleElement &&
      this.popupButtonElement
    ) {
      this.setupTitleObserver(this.popupTitleElement);
      this.setupButtonObserver(this.popupButtonElement);

      observer.disconnect();
    }
  };

  setupTitleObserver(title: HTMLElement) {
    this.titleObserver = new MutationObserver(this.onTitleObserve);
    this.titleObserver.observe(title, { subtree: true, childList: true });

    this.extractTitleAndTransform();
  }

  onTitleObserve: MutationCallback = () => {
    this.extractTitleAndTransform();
  };

  setupButtonObserver(button: HTMLElement) {
    this.buttonObserver = new MutationObserver(this.onButtonObserve);
    this.buttonObserver.observe(button, { subtree: true, childList: true });

    this.extractButtonAndTransform();
  }

  onButtonObserve: MutationCallback = () => {
    this.extractButtonAndTransform();
  };

  splitTitle(title: string): TitleLine[] {
    const lines = title.split(' - ');

    if (lines.length === 1) {
      return [{ content: lines[0], index: 3 }];
    }

    if (lines.length === 2) {
      return [
        { content: lines[0], index: 2 },
        { content: lines[1], index: 3 },
      ];
    }

    return lines
      .slice(0, 3)
      .map((line, index) => ({ content: line, index: index + 1 }));
  }

  getSplitTitleElement(lines: TitleLine[]): HTMLElement[] {
    return lines.map(({ content, index }) => {
      const span = document.createElement('span');

      span.classList.add('line', `line--${index}`);
      span.dataset.style = lineStyles[(index - 1) % lineStyles.length];
      span.innerHTML = content;

      return span;
    });
  }

  getStyledTitleElement(lines: HTMLElement[]): HTMLElement {
    const spanContainer = document.createElement('span');

    spanContainer.classList.add('mm-title', 'mm-title--important');
    spanContainer.append(...lines);

    return spanContainer;
  }

  extractTitleAndTransform() {
    const title = this.popupTitleElement;

    if (title) {
      const textNode = Array.from(title.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE,
      );

      if (textNode && textNode.textContent) {
        this.transformTitle(textNode.textContent);
      }
    }
  }

  transformTitle(text: string) {
    const sourceTitle = this.popupTitleElement;

    if (sourceTitle) {
      const splitTitle = this.splitTitle(text.trim());
      const titleLines = this.getSplitTitleElement(splitTitle);
      const innerTitle = this.getStyledTitleElement(titleLines);

      sourceTitle.innerHTML = '';
      sourceTitle.appendChild(innerTitle);

      return true;
    }

    return false;
  }

  extractButtonAndTransform() {
    const button = this.popupButtonElement;

    if (button) {
      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE,
      );

      if (textNode && textNode.textContent) {
        this.transformButton(textNode.textContent);
      }
    }
  }

  transformButton(text: string) {
    const button = this.popupButtonElement;

    if (button) {
      const contentContainer = document.createElement('div');

      button.innerHTML = '';
      button.classList.add('mm-cta', 'mm-cta--important');

      contentContainer.classList.add('mm-cta__content');
      contentContainer.textContent = text;

      button.appendChild(contentContainer);

      ['above', 'below'].forEach((layer) => {
        const layerContainer = document.createElement('div');

        layerContainer.classList.add('mm-cta__border', layer);

        layerContainer.innerHTML = [
          borderTop,
          borderBottom,
          borderLeft,
          borderRight,
        ].join('\n');

        button.appendChild(layerContainer);
      });
    }
  }
}
